// ============================================================================
// Authenticated Tasks Webform resource bridge
// ============================================================================
// /dailys/tasks is a login-required surface, while its presenter intentionally
// remains in src/webforms without AuthContext coupling. This is the one narrow
// app-side seam between those constraints: it owns authenticated QueryScope
// through useRouteResource and injects plain lookup props into the form.
//
// Do not move this into src/webforms. A webform module must never be able to
// choose a scope for authenticated farm tables. The outer gate also means a
// logged-out state unmounts the resource consumer before it can build a public
// query key, rather than merely disabling a public-scope request.
//
// BOTH READS BEHIND THIS SEAM ARE ALREADY SERVER-ENFORCED. Migration 193 (live)
// revoked anon EXECUTE on list_eligible_assignees and dropped anon read on
// webform_config, so this bridge is not what makes the surface authenticated —
// it is what stops the presenter from pretending otherwise.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {RESOURCE} from './routeDataCatalog.js';
import type {TasksWebformAssigneesData} from './routeDataCatalog.js';
import {useRouteResource} from './useRouteResource.js';
import type {TasksWebformResourceProps} from '../../webforms/TasksWebform.js';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';

type TasksWebformAuthenticatedBridgeProps = {
  children: React.ReactElement<TasksWebformResourceProps>;
};

type AuthenticatedTasksWebformResourcesProps = TasksWebformAuthenticatedBridgeProps & {
  sessionSubmitter: SessionSubmitter;
};

const TASKS_WEBFORM_RESOURCE_PARAMS = Object.freeze({});

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : String(error);
}

function AuthenticatedTasksWebformResources({children, sessionSubmitter}: AuthenticatedTasksWebformResourcesProps) {
  const {data, loading, error, refetch} = useRouteResource(
    RESOURCE.TASKS_WEBFORM_ASSIGNEES,
    TASKS_WEBFORM_RESOURCE_PARAMS,
  );

  // The resource runtime intentionally retains a prior successful value on a
  // same-key retry. That is right for a record editor and WRONG for this
  // availability filter: a retained list is a list whose hidden-people policy
  // may no longer have been applied. Mask it whenever the compound lookup
  // failed, so no stale or unfiltered assignee stays selectable during a retry.
  const lookup = error ? null : (data as TasksWebformAssigneesData | null);
  const assigneeOptions = lookup?.assignees || [];
  const assigneeOptionsError = error ? `Could not load form options: ${errorMessage(error)}` : null;

  return React.cloneElement(children, {
    assigneeOptions,
    assigneeOptionsLoading: loading,
    assigneeOptionsError,
    retryAssigneeOptions: refetch,
    sessionSubmitter,
  });
}

export default function TasksWebformAuthenticatedBridge({children}: TasksWebformAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  // App's LoginScreen gate normally makes this branch unreachable. Keep the
  // local fail-closed guard anyway: a future pre-gate mount must not turn this
  // authenticated resource into a public-scope query.
  if (authState === null || authState === false) return null;

  // The Task form's LockedSubmitter control and its RPC attribution are keyed
  // by this exact display-name fallback, which App previously threaded through
  // WebformsEntry. Keeping identity with the authenticated bridge alongside the
  // data resource leaves the presenter a plain, auth-free component.
  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return (
    <AuthenticatedTasksWebformResources sessionSubmitter={sessionSubmitter}>
      {children}
    </AuthenticatedTasksWebformResources>
  );
}
